import { useTheme } from "@/src/styles/theme-provider";
import { ArrowCircleLeft, ArrowCircleRight } from "iconsax-react-nativejs";
import React from "react";
import { useTranslation } from "react-i18next";
import { Pressable, Text, useWindowDimensions, View } from "react-native";

type CustomPaginationProps = Readonly<{
  currentPage: number;
  totalPages: number;
  pageSize?: number;
  totalItems: number;
  from?: number;
  to?: number;
  onPageChange: (page: number) => void;
}>;

const ScPagination = ({
  currentPage,
  totalPages,
  pageSize = 6,
  from,
  to,
  totalItems,
  onPageChange,
}: CustomPaginationProps) => {
  const { t } = useTranslation();
  const { themeVars } = useTheme();
  const { width } = useWindowDimensions();
  const isMobile = width < 768;

  const startItem =
    from ?? (totalItems === 0 ? 0 : (currentPage - 1) * pageSize + 1);
  const endItem =
    to ?? (totalItems === 0 ? 0 : Math.min(currentPage * pageSize, totalItems));

  const isPrevDisabled = currentPage === 1;
  const isNextDisabled = currentPage >= totalPages;

  if (isMobile) {
    return (
      <View className="flex-row justify-between items-center px-4 py-3 border-t border-ash-3">
        <Pressable
          disabled={isPrevDisabled}
          onPress={() => onPageChange(currentPage - 1)}
          className="p-1.5 hover:bg-ash-3 hover:rounded-full"
        >
          <ArrowCircleLeft
            size={26}
            color={
              isPrevDisabled
                ? (themeVars["--color-ash-3"] as string)
                : (themeVars["--color-primary-default"] as string)
            }
          />
        </Pressable>

        <View className="items-center">
          <Text className="text-xs text-ash-1">
            {startItem}–{endItem}{" "}
            <Text className="text-xs text-ash-1">
              {startItem} {t("LABEL_TO")}{endItem} {t("LABEL_OF")} {totalItems}
            </Text>
          </Text>
        </View>

        <Pressable
          disabled={isNextDisabled}
          onPress={() => onPageChange(currentPage + 1)}
          className="p-1.5 hover:bg-ash-3 hover:rounded-full"
        >
          <ArrowCircleRight
            size={26}
            color={
              isNextDisabled
                ? (themeVars["--color-ash-3"] as string)
                : (themeVars["--color-primary-default"] as string)
            }
          />
        </Pressable>
      </View>
    );
  }

  const pages: (number | "...")[] = [];
  if (totalPages <= 7) {
    for (let i = 1; i <= totalPages; i++) pages.push(i);
  } else {
    pages.push(1);
    if (currentPage > 4) pages.push("...");
    const start = Math.max(2, currentPage - 1);
    const end = Math.min(totalPages - 1, currentPage + 1);
    for (let i = start; i <= end; i++) pages.push(i);
    if (currentPage < totalPages - 3) pages.push("...");
    pages.push(totalPages);
  }

  return (
    <View className="flex-row justify-between items-center px-6 py-3 border-t border-ash-3">
      {/* Info */}
      <Text className="text-xs text-ash-1">
        {startItem}–{endItem}{" "}
        <Text className="text-ash-2">{t("LABEL_OF")}</Text>{" "}
        <Text className="text-onyx font-medium">{totalItems}</Text>
      </Text>

      {/* Controls */}
      <View className="flex-row items-center gap-1">
        <Pressable
          disabled={isPrevDisabled}
          onPress={() => onPageChange(currentPage - 1)}
          className="p-1 hover:bg-ash-3 hover:rounded-full"
        >
          <ArrowCircleLeft
            size={22}
            color={
              isPrevDisabled
                ? (themeVars["--color-ash-3"] as string)
                : (themeVars["--color-primary-default"] as string)
            }
          />
        </Pressable>

        {pages.map((page, idx) =>
          page === "..." ? (
            <Text key={idx} className="w-7 text-center text-xs text-ash-1">
              ···
            </Text>
          ) : (
            <Pressable
              key={idx}
              onPress={() => onPageChange(Number(page))}
              style={{ width: 28, height: 28, borderRadius: 14 }}
              className={`items-center justify-center ${
                page === currentPage
                  ? "bg-primary hover:opacity-90"
                  : "hover:bg-ash-3"
              }`}
            >
              <Text
                className={`text-xs font-medium ${
                  page === currentPage ? "text-chalk" : "text-slate"
                }`}
              >
                {page}
              </Text>
            </Pressable>
          ),
        )}

        <Pressable
          disabled={isNextDisabled}
          onPress={() => onPageChange(currentPage + 1)}
          className="p-1 hover:bg-ash-3 hover:rounded-full"
        >
          <ArrowCircleRight
            size={22}
            color={
              isNextDisabled
                ? (themeVars["--color-ash-3"] as string)
                : (themeVars["--color-primary-default"] as string)
            }
          />
        </Pressable>
      </View>
    </View>
  );
};

export default ScPagination;
